import { subscriptionApi } from '@/requests/api';
import { QueryKey } from '@/requests/query-keys';
import type {
  SetSubscriptionEndDateRequest,
  SubscriptionStatusFilter,
  SubscriptionType
} from '@/types/types';
import { isConflictError } from '@/utils/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type SetSubscriptionEndDateVariables = {
  id: string;
  // Not sent to the API (the endpoint only needs the subscription id) — carried so onSuccess can
  // invalidate that one customer's own detail cache, the same way useCustomerDeleteMutation's
  // variables double as an invalidation key (hooks/use-customers.ts).
  customerId: string;
  request: SetSubscriptionEndDateRequest;
};

// REQ-8: the subscriptions list (its own endpoint, REQ-6/REQ-8) and the upcoming-actions list the
// Dashboard also reads — both share the one `SubscriptionListRow` shape (apps/api/README.md § API
// Conventions), so both live in this resource's one hook file alongside the end-date mutation
// (react-base/RULES.md § Hooks).
export const useSubscriptionsQuery = (
  page: number,
  size: number,
  options: {
    type?: SubscriptionType;
    search?: string;
    status?: SubscriptionStatusFilter;
    sort?: string;
  } = {}
) =>
  useQuery({
    queryKey: [QueryKey.SUBSCRIPTIONS, { page, size, ...options }],
    queryFn: () => subscriptionApi.getSubscriptions(page, size, options),
    placeholderData: keepPreviousData
  });

export const useUpcomingActionsQuery = (page: number, size: number) =>
  useQuery({
    queryKey: [QueryKey.UPCOMING_ACTIONS, { page, size }],
    queryFn: () => subscriptionApi.getUpcomingActions(page, size),
    placeholderData: keepPreviousData
  });

// REQ-9/REQ-10: the administrator's one subscription write — now version-checked (the subscription
// response DTOs expose `version` as of 2026-10-24, docs/session-handoff.md). On a 409 the relevant
// queries are invalidated the same way a success does, so the background reload use-customers.ts
// and use-orders.ts already do for their own entities also reloads this subscription's fresh
// version — the caller's own `onError` shows the conflict notice and keeps the typed draft.
export const useSubscriptions = () => {
  const queryClient = useQueryClient();

  const invalidateSubscription = (customerId: string) => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOMERS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOMER, customerId] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.SUBSCRIPTIONS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.UPCOMING_ACTIONS] });
  };

  const setEndDate = useMutation<void, Error, SetSubscriptionEndDateVariables>({
    mutationFn: ({ id, request }) => subscriptionApi.setEndDate(id, request),
    onSuccess: (_, { customerId }) => invalidateSubscription(customerId),
    onError: (error, { customerId }) => {
      if (isConflictError(error)) invalidateSubscription(customerId);
    }
  });

  return { setEndDate };
};
